import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { OLYMP_API_BASE_URL } from '../config/api';
import { normalizeLanguage } from '../i18n/languages';
import i18n from '../i18n/index';
import { useAuth } from '../auth/authContext';

const DEFAULT_APPEARANCE = {
  theme: 'light',
  primaryColor: '#FF7A00',
  language: 'en',
  fontScale: 1,
  animationsEnabled: true,
  dateFormat: 'dd.mm.yyyy',
  timezone: 'Europe/Berlin',
  defaultHomepage: 'dashboard',
  dashboardWidgets: {
    incomingRequests: true,
    statistics: true,
    runningAnalyses: true,
    paymentOverview: true,
    activityLog: true,
  },
};

const HOMEPAGE_PATHS = {
  dashboard: '/',
  requests: '/requests',
  negotiations: '/catalog',
  executions: '/requests',
};

const AppearanceContext = createContext(null);

export const resolveThemeMode = (theme) => {
  if (theme === 'dark') return 'dark';
  if (theme === 'system') {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

const getCacheKey = (userEmail) => `appearance_settings_v1:${userEmail || 'anonymous'}`;

const applyAppearanceCSS = (settings) => {
  const root = document.documentElement;
  root.style.setProperty('--font-scale', settings.fontScale ?? 1);
  if (settings.animationsEnabled === false) {
    root.style.setProperty('--motion-reduce', '1');
  } else {
    root.style.removeProperty('--motion-reduce');
  }
};

export const AppearanceProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  const [appearance, setAppearance] = useState(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const cached = localStorage.getItem(getCacheKey(storedUser?.email));
      if (cached) return { ...DEFAULT_APPEARANCE, ...JSON.parse(cached) };
    } catch (_) {}
    return DEFAULT_APPEARANCE;
  });

  const fetchAndApply = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      const { data } = await axios.get(
        `${OLYMP_API_BASE_URL}/olymp/settings/current/`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const remote = data?.appearanceSettings;
      if (!remote) return;

      const merged = { ...DEFAULT_APPEARANCE, ...remote };

      const email = user?.email || JSON.parse(localStorage.getItem('user') || '{}')?.email;
      if (email) {
        localStorage.setItem(getCacheKey(email), JSON.stringify(merged));
      }

      applyAppearanceCSS(merged);

      const lang = normalizeLanguage(merged.language);
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }

      setAppearance(merged);
    } catch (_) {
      // Fall back to defaults silently
    }
  }, [user]);

  // Fetch fresh settings whenever the user authenticates
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchAndApply();
  }, [isAuthenticated, fetchAndApply]);

  // Apply CSS variables whenever appearance changes
  useEffect(() => {
    applyAppearanceCSS(appearance);
  }, [appearance]);

  // Re-evaluate system theme when OS preference changes
  useEffect(() => {
    if (appearance.theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setAppearance((prev) => ({ ...prev }));
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [appearance.theme]);

  const refreshAppearance = useCallback(() => fetchAndApply(), [fetchAndApply]);

  // Apply a patch immediately (live preview in Settings) without persisting to API
  const updateAppearance = useCallback((patch) => {
    setAppearance((prev) => {
      const merged = { ...prev, ...patch };
      applyAppearanceCSS(merged);
      const lang = normalizeLanguage(merged.language);
      if (i18n.language !== lang) i18n.changeLanguage(lang);
      return merged;
    });
  }, []);

  const getHomepagePath = useCallback(
    () => HOMEPAGE_PATHS[appearance.defaultHomepage] || '/',
    [appearance.defaultHomepage],
  );

  const formatDate = useCallback(
    (date, options = {}) => {
      if (!date) return '';
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return String(date);
      try {
        return new Intl.DateTimeFormat(undefined, {
          timeZone: appearance.timezone || undefined,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          ...options,
        }).format(d);
      } catch (_) {
        return d.toLocaleDateString();
      }
    },
    [appearance.timezone],
  );

  const themeMode = resolveThemeMode(appearance.theme);

  return (
    <AppearanceContext.Provider
      value={{ appearance, themeMode, refreshAppearance, updateAppearance, getHomepagePath, formatDate }}
    >
      {children}
    </AppearanceContext.Provider>
  );
};

export const useAppearance = () => useContext(AppearanceContext);
